import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

import { PennButton, PennText } from "@/components/ui";
import { useAppDispatch } from "@/stores/hooks";
import { setLogout } from "@/api/redux-toolkit/reducer/authSlice";
import { cognitoSignOut } from "@/services/cognito-auth";
import { OIDC_ENABLED, endSession } from "@penn/web-common";
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "@/config/auth-storage-keys";
import { authStorage } from "@/config/session-storage";

/**
 * Shown (instead of any admin page) when a signed-in user lacks the admin
 * console permissions — see `RequireAdminAccess`. Deliberately a standalone,
 * chrome-less page: no sidebar, no header, no dataset routes are rendered.
 *
 * The only action offered is "Sign out" so a wrong-account user isn't trapped
 * and can sign in with an account that does have access.
 */
export const NoAdminAccessPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  // Mirrors AdminHeader's logout: tear down both the Penn session and the
  // Amplify-managed Cognito session, then drop the cached `/me` so the next
  // account starts clean.
  const handleSignOut = async () => {
    await cognitoSignOut();
    dispatch(setLogout());
    authStorage.removeItem(TOKEN_STORAGE_KEY);
    authStorage.removeItem(USER_STORAGE_KEY);
    queryClient.clear();
    if (OIDC_ENABLED) {
      // Platform sign-in: the account switch this page exists for only works if
      // the ISSUER session ends too — /login would otherwise resume the same
      // account from the SSO cookie. Full-page navigation.
      endSession();
      return;
    }
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50 dark:bg-gray-950">
      <div className="text-center max-w-md">
        <PennText as="h1" size="xl" align="center" color="blue_grey1" font="semibold" className="mb-2">
        You are not authorized to access this page
        </PennText>
        <PennText as="p" size="sm" align="center" color="blue_grey" className="mb-8">
        If you believe this is a mistake, contact your administrator.
        </PennText>
        <PennButton variant="secondary" size="md" onClick={handleSignOut}>
          Sign out
        </PennButton>
      </div>
    </div>
  );
};
